import { Component, Input, OnInit, OnDestroy, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import Chart from 'chart.js/auto';
import type { ChartConfiguration, ChartType } from 'chart.js/auto';
import { DashCard, DashChart, DashComponentConfig } from '../../interfaces/dash-component.interface';

@Component({
  selector: 'app-dash-component',
  templateUrl: './dash-component.component.html',
  styleUrls: ['./dash-component.component.css']
})
export class DashComponentComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() config?: DashComponentConfig;
  @Input() firstRowCards: DashCard[] = [];
  @Input() firstRowChart?: DashChart;
  @Input() secondRowChart?: DashChart;
  @Input() secondRowCards: DashCard[] = [];

  private charts: Chart[] = [];
  private componentId = Math.random().toString(36).substr(2, 9);
  private viewInitialized = false;

  constructor() { }

  ngOnInit(): void {
    // Se config for fornecido, usar seus dados
    this.applyConfig();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    // Aguardar o próximo ciclo para garantir que os elementos estão no DOM
    setTimeout(() => {
      this.recreateCharts();
    }, 500);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando os dados chegam depois (HTTP), é comum o chart já existir com dataset vazio.
    // Recriar garante que Chart.js reflita o novo config/data.
    if (changes['config'] || changes['firstRowChart'] || changes['secondRowChart']) {
      this.applyConfig();

      if (this.viewInitialized) {
        // Pequeno delay para garantir que o DOM já refletiu o novo config
        setTimeout(() => this.recreateCharts(), 0);
      }
    }
  }

  ngOnDestroy(): void {
    // Destruir charts para evitar vazamento de memória
    this.destroyCharts();
  }

  private applyConfig(): void {
    if (!this.config) return;
    this.firstRowCards = this.config.firstRowCards;
    this.firstRowChart = this.config.firstRowChart;
    this.secondRowChart = this.config.secondRowChart;
    this.secondRowCards = this.config.secondRowCards;
  }

  private destroyCharts(): void {
    this.charts.forEach(chart => chart.destroy());
    this.charts = [];
  }

  private recreateCharts(): void {
    this.destroyCharts();

    // Criar primeiro gráfico
    if (this.firstRowChart) {
      this.createChart(`firstChart_${this.componentId}`, this.firstRowChart);
    }

    // Criar segundo gráfico
    if (this.secondRowChart) {
      this.createChart(`secondChart_${this.componentId}`, this.secondRowChart);
    }
  }

  private createChart(canvasId: string, chartConfig: DashChart): void {
    // Aguardar um pouco mais para garantir que o DOM está pronto
    setTimeout(() => {
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
      
      if (canvas) {
        try {
          // Limpar canvas anterior se existir
          const existingChart = Chart.getChart(canvas);
          if (existingChart) {
            existingChart.destroy();
          }

          // Configuração do gráfico
          const config: ChartConfiguration = {
            type: chartConfig.type as ChartType,
            data: chartConfig.data,
            options: {
              responsive: true,
              maintainAspectRatio: false,
              interaction: {
                intersect: false,
              },
              plugins: {
                legend: {
                  position: 'bottom',
                  display: true
                }
              },
              ...chartConfig.options
            }
          };

          const chart = new Chart(canvas, config);
          this.charts.push(chart);
          
          // Forçar atualização
          chart.update();
          
        } catch (error) {
          console.error(`Erro ao criar gráfico ${canvasId}:`, error);
        }
      }
    }, 100);
  }

  getCardColorClass(color: string): string {
    return `card-${color}`;
  }

  getTrendIcon(isPositive: boolean): string {
    return isPositive ? 'fa-arrow-up' : 'fa-arrow-down';
  }

  getTrendColorClass(isPositive: boolean): string {
    return isPositive ? 'text-success' : 'text-danger';
  }

  getFirstChartId(): string {
    return `firstChart_${this.componentId}`;
  }

  getSecondChartId(): string {
    return `secondChart_${this.componentId}`;
  }
}
